import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Building2, Loader2, FileText, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useCart, itemPrice, itemPack } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Reveal, EASE } from "@/components/motion";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const FIELD = "w-full border hairline bg-white px-4 py-3 text-sm rounded-xl focus:outline-none focus:border-[#0F5A47] transition-colors";

export default function Checkout() {
  const { items, total, hasRx, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "", business_name: "" });
  const [step, setStep] = useState(1);
  const [paying, setPaying] = useState(false);
  const [order, setOrder] = useState(null);
  const [dl, setDl] = useState("");
  const [gst, setGst] = useState("");
  const [savingB2B, setSavingB2B] = useState(false);
  const [mockMode, setMockMode] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [refApplied, setRefApplied] = useState(null);
  const [refStatus, setRefStatus] = useState(""); // "" | checking | ok | invalid

  useEffect(() => {
    api.get("/config").then((r) => setMockMode(!!r.data.payments_mock)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const detailsValid = useMemo(() =>
    form.name.trim() && form.phone.trim().length >= 10
  , [form]);

  const deliveryValid = useMemo(() => {
    const addrOk = form.business_name.trim() && form.address.trim() && form.city.trim() && form.state.trim() && form.pincode.trim().length >= 6;
    const emailOk = !form.email.trim() || /\S+@\S+\.\S+/.test(form.email);
    const licenseOk = !order?.requires_dl_gst || (dl.trim().length >= 5 && gst.trim().length === 15);
    return addrOk && emailOk && licenseOk;
  }, [form, dl, gst, order]);

  const pricing = useMemo(() => {
    const discount = refApplied ? Math.round(total * refApplied.discount_pct) / 100 : 0;
    const taxable = Math.round((total - discount) * 100) / 100;
    const gstAmt = Math.round(taxable * 0.05 * 100) / 100;
    return { discount, gst: gstAmt, grand: Math.round((taxable + gstAmt) * 100) / 100 };
  }, [total, refApplied]);

  // Auto-validate the referral code as the buyer types — a valid code applies itself, no button
  useEffect(() => {
    const code = refInput.trim();
    if (!code) { setRefApplied(null); setRefStatus(""); return; }
    setRefStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/referrals/validate", { code });
        setRefApplied(data);
        setRefStatus("ok");
        toast.success(`Code ${data.code} applied — ${data.discount_pct}% extra discount`);
      } catch {
        setRefApplied(null);
        setRefStatus("invalid");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [refInput]);

  const finalizeOrder = (ord) => {
    clear();
    setOrder(ord);
    setStep(ord.customer?.address ? 4 : 3);
  };

  const pay = async () => {
    setPaying(true);
    try {
      const payload = {
        buyer_type: "b2b",
        customer: form,
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty, variant: i.variantLabel || null })),
        referral_code: refApplied?.code || null,
      };
      const { data: created } = await api.post("/orders", payload);

      if (created.mock) {
        const { data } = await api.post(`/orders/${created.order_id}/verify-payment`, {
          razorpay_order_id: created.razorpay_order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock",
        });
        toast.success("Demo payment successful");
        finalizeOrder(data.order);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay checkout");
      const rzp = new window.Razorpay({
        key: created.razorpay_key_id,
        amount: created.amount,
        currency: created.currency,
        name: "Kanan Remedies",
        description: `Order ${created.order_number}`,
        order_id: created.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#0F5A47" },
        handler: async (resp) => {
          try {
            const { data } = await api.post(`/orders/${created.order_id}/verify-payment`, resp);
            toast.success("Payment successful");
            finalizeOrder(data.order);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const submitB2B = async () => {
    setSavingB2B(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/b2b-details`, {
        dl_number: dl,
        gst_number: gst,
        business_name: form.business_name,
        address: form.address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        email: form.email,
      });
      setOrder(data.order);
      setStep(4);
      toast.success("Delivery & license details recorded. Order confirmed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingB2B(false);
    }
  };

  if (items.length === 0 && !order) return (
    <div className="pt-40 pb-32 text-center" data-testid="checkout-empty">
      <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Your cart is empty</p>
      <Link to="/shop" className="inline-block mt-6 bg-emerald-800 text-white px-8 py-4 rounded-xl font-bold text-sm">Browse Catalog</Link>
    </div>
  );

  const StepDot = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2">
      <span className={`w-7 h-7 rounded-full flex items-center justify-center font-mono-ed text-[11px] border ${done ? "bg-emerald-700 text-white border-emerald-700" : active ? "border-emerald-700 text-emerald-800" : "text-slate-300"}`}>{done ? "✓" : n}</span>
      <span className={`font-mono-ed text-[10px] uppercase tracking-[0.15em] hidden sm:inline ${active ? "text-[#0B0F17]" : "text-slate-400"}`}>{label}</span>
    </div>
  );

  return (
    <div className="max-w-[1100px] mx-auto px-5 sm:px-10 pt-32 pb-20" data-testid="checkout-page">
      <Reveal>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47] mb-3">Secure B2B Checkout</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Finalize Order</h1>
      </Reveal>

      <div className="flex gap-6 mt-10 mb-12 flex-wrap" data-testid="checkout-stepper">
        <StepDot n={1} label="Business Details" active={step === 1} done={step > 1} />
        <StepDot n={2} label="Payment" active={step === 2} done={step > 2} />
        <StepDot n={3} label="Delivery + License" active={step === 3} done={step > 3} />
        <StepDot n={4} label="Confirmed" active={step === 4} done={false} />
      </div>

      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: EASE }}>
                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 mb-8" data-testid="b2b-checkout-notice">
                  <Building2 size={18} className="text-emerald-800 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-emerald-900 leading-relaxed">
                    <span className="font-bold">Trade ordering for licensed buyers.</span> Just your name and mobile number to place the order — business name, delivery address{hasRx && ", Drug License & GST"} and everything else is collected right after payment. A GST invoice is issued with every order.
                  </p>
                </div>

                <div className="grid gap-4">
                  <input data-testid="checkout-name-input" className={FIELD} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="checkout-phone-input" className={FIELD} placeholder="Mobile number (10 digits)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="mt-4">
                  <input
                    data-testid="checkout-referral-input"
                    className={FIELD}
                    placeholder="Referral code (optional)"
                    value={refInput}
                    onChange={(e) => setRefInput(e.target.value)}
                  />
                  {refStatus === "checking" && refInput.trim().length >= 3 && (
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5" data-testid="checkout-referral-checking"><Loader2 size={11} className="animate-spin" /> Checking code…</p>
                  )}
                  {refStatus === "ok" && refApplied && (
                    <p className="text-xs text-emerald-700 font-semibold mt-2" data-testid="checkout-referral-status">
                      {refApplied.code} applied — {refApplied.discount_pct}% extra discount (−{inr(pricing.discount)}) on this order
                    </p>
                  )}
                  {refStatus === "invalid" && refInput.trim().length >= 3 && (
                    <p className="text-xs text-red-600 mt-2" data-testid="checkout-referral-invalid">Code not recognized — check with your salesperson</p>
                  )}
                </div>
                {!user && (
                  <p className="text-xs text-slate-400 mt-4">Ordering as a guest business. <Link to="/login" className="text-emerald-800 underline underline-offset-2">Sign in</Link> to save your order history.</p>
                )}
                <button
                  data-testid="checkout-continue-btn"
                  disabled={!detailsValid}
                  onClick={() => setStep(2)}
                  className="mt-8 bg-emerald-800 text-white px-10 py-4 rounded-xl font-bold text-sm hover:bg-emerald-900 transition-colors duration-300 active:scale-95 disabled:opacity-40"
                >
                  Continue to Payment
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: EASE }}>
          {mockMode ? (
            <div className="border border-amber-300 bg-amber-50/70 rounded-2xl p-8" data-testid="payment-unconfigured-panel">
              <div className="flex items-start gap-3">
                <ShieldAlert size={22} className="text-amber-800 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold text-lg text-amber-950">Online Payment Gateway Configuration in Progress</h3>
                  <p className="text-xs text-amber-900 mt-2 leading-relaxed">
                    Razorpay live API credentials are being verified for this environment. To ensure strict compliance and prevent any unverified transactions, online self-checkout is locked.
                  </p>
                  <p className="text-xs text-amber-900 mt-2 font-medium">
                    You can place your wholesale order directly via WhatsApp — our team will confirm stock and share the official payment QR code / bank transfer details with an instant invoice.
                  </p>
                </div>
              </div>
              <a
                data-testid="checkout-whatsapp-fallback-btn"
                href={`https://wa.me/918852832320?text=${encodeURIComponent(
                  `Hello Kanan Remedies, I would like to place an order:\n\n` +
                  items.map((i) => `• ${i.product.name} (${itemPack(i)}) × ${i.qty} = ${inr(itemPrice(i) * i.qty)}`).join("\n") +
                  `\n\nSubtotal: ${inr(total)}${refApplied ? `\nReferral (${refApplied.code}): −${inr(pricing.discount)}` : ""}\nGST (5%): ${inr(pricing.gst)}\nTotal: ${inr(pricing.grand)}\n\nContact:\n${form.name}\nPhone: ${form.phone}`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5B] text-white py-4 rounded-xl font-bold text-sm shadow-md transition-all active:scale-[0.98]"
              >
                Place This Order on WhatsApp (+91 8852832320)
              </a>
              <button onClick={() => setStep(1)} className="mt-4 font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-800 transition-colors" data-testid="payment-back-btn">← Back to Details</button>
            </div>
          ) : (
            <div className="border border-slate-200 bg-white rounded-2xl p-8">
              <h3 className="font-display font-bold text-xl">Razorpay Secure Payment</h3>
              <p className="text-xs text-slate-500 mt-2">Cards, UPI, netbanking & wallets. 256-bit encrypted via Razorpay.</p>
              {hasRx && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mt-5" data-testid="b2b-rx-notice">
                  <FileText size={15} className="text-amber-700 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-900 leading-relaxed">Rx items in this order: no prescription needed. You will be asked for your delivery address, Drug License & GST number immediately after payment.</p>
                </div>
              )}
              <button
                data-testid="razorpay-pay-now-button"
                onClick={pay}
                disabled={paying}
                className="mt-8 w-full bg-emerald-800 text-white py-4 rounded-xl font-bold text-sm hover:bg-emerald-900 transition-colors duration-300 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {paying && <Loader2 size={14} className="animate-spin" />}
                Pay {inr(pricing.grand)} with Razorpay (incl. 5% GST{refApplied ? `, ${refApplied.discount_pct}% referral off` : ""})
              </button>
              <button onClick={() => setStep(1)} className="mt-4 font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-800 transition-colors" data-testid="payment-back-btn">← Back</button>
            </div>
          )}
              </motion.div>
            )}

            {step === 3 && order && (
              <motion.div key="s3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: EASE }}>
                <div className="border border-amber-300 bg-amber-50/60 rounded-2xl p-8">
                  <h3 className="font-display font-bold text-xl">Almost done — delivery {order.requires_dl_gst ? "& license" : ""} details</h3>
                  <p className="text-xs text-slate-600 mt-2">
                    Payment received for order <span className="font-mono-ed">{order.order_number}</span>. Tell us where to deliver your stock.
                    {order.requires_dl_gst && " Regulations also require your Drug License and GST details before we dispatch Rx items."}
                  </p>

                  <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-6 mb-3">Business & Delivery Details</p>
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="delivery-address-form">
                    <input data-testid="b2b-business-name-input" className={`${FIELD} sm:col-span-2`} placeholder="Business / shop / clinic name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                    <input data-testid="b2b-address-input" className={`${FIELD} sm:col-span-2`} placeholder="Shop / clinic street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                    <input data-testid="b2b-city-input" className={FIELD} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    <input data-testid="b2b-state-input" className={FIELD} placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                    <input data-testid="b2b-pincode-input" className={FIELD} placeholder="PIN code (6 digits)" maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                    <input data-testid="b2b-email-input" className={FIELD} placeholder="Email (optional — for order updates)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>

                  {order.requires_dl_gst && (
                    <div className="grid gap-4 mt-6">
                      <div>
                        <label className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Drug License (DL) Number</label>
                        <input data-testid="b2b-dl-number-input" className={FIELD} placeholder="e.g. MH-PH-20B-123456" value={dl} onChange={(e) => setDl(e.target.value)} />
                      </div>
                      <div>
                        <label className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">GST Number (15 characters)</label>
                        <input data-testid="b2b-gst-number-input" className={FIELD} placeholder="e.g. 27AAACK1234A1Z5" maxLength={15} value={gst} onChange={(e) => setGst(e.target.value)} />
                      </div>
                    </div>
                  )}
                  <button
                    data-testid="b2b-submit-btn"
                    onClick={submitB2B}
                    disabled={savingB2B || !deliveryValid}
                    className="mt-6 bg-emerald-800 text-white px-10 py-4 rounded-xl font-bold text-sm hover:bg-emerald-900 transition-colors duration-300 active:scale-95 disabled:opacity-40 flex items-center gap-2"
                  >
                    {savingB2B && <Loader2 size={14} className="animate-spin" />} Submit & Confirm Order
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && order && (
              <motion.div key="s4" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="text-center py-10">
                <CheckCircle2 size={56} className="mx-auto text-emerald-700" />
                <h3 className="font-display font-extrabold text-3xl mt-6" data-testid="order-confirmed-heading">Order Confirmed</h3>
                <p className="font-mono-ed text-sm text-slate-500 mt-2">{order.order_number}</p>
                <p className="text-sm text-slate-500 mt-4 max-w-md mx-auto">Your GST invoice will accompany dispatch. Our team will confirm stock and ship your order with cold-chain care.</p>
                <div className="flex justify-center gap-4 mt-8 flex-wrap">
                  <button onClick={() => navigate(`/order/${order.id}?${order.customer.email ? `email=${encodeURIComponent(order.customer.email)}` : `phone=${encodeURIComponent(order.customer.phone)}`}`)} data-testid="view-order-btn" className="bg-emerald-800 text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-emerald-900 transition-colors active:scale-95">View Order</button>
                  <Link to="/shop" className="border border-slate-200 px-8 py-4 rounded-xl font-bold text-sm hover:border-emerald-600 transition-colors">Continue Shopping</Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* SUMMARY */}
        <div className="lg:col-span-2">
          <div className="border border-slate-200 bg-white rounded-2xl p-6 sticky top-32" data-testid="checkout-summary">
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-4">Order Summary</p>
            {(order ? order.items : items.map((i) => ({ name: i.product.name, pack: itemPack(i), qty: i.qty, price: itemPrice(i), line_total: itemPrice(i) * i.qty, rx: i.product.rx }))).map((i, idx) => (
              <div key={idx} className="flex justify-between gap-3 py-2.5 border-b hairline last:border-0 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{i.name} {i.rx && <span className="font-mono-ed text-[8px] text-amber-800 border border-amber-300 bg-amber-50 px-1 ml-1 rounded">Rx</span>}</p>
                  <p className="font-mono-ed text-[10px] text-slate-400">{(() => { const bx = i.boxes ?? i.qty * (parseInt(i.pack, 10) || 1); return `${bx} boxes (${(bx * 10).toLocaleString("en-IN")} strips)`; })()}</p>
                </div>
                <span className="font-mono-ed text-sm flex-shrink-0">{inr(i.line_total)}</span>
              </div>
            ))}
            <div className="mt-4 pt-4 border-t hairline font-mono-ed space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500">Subtotal (Net Rate)</span>
                <span data-testid="checkout-subtotal">{inr(order ? (order.subtotal ?? order.total) : total)}</span>
              </div>
              {(order ? (order.discount_amount || 0) : pricing.discount) > 0 && (
                <div className="flex justify-between text-sm text-emerald-700">
                  <span className="uppercase text-[11px] tracking-[0.2em]">Referral Discount ({order ? order.referral_code : refApplied?.code})</span>
                  <span data-testid="checkout-discount">−{inr(order ? order.discount_amount : pricing.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500">GST (5%)</span>
                <span data-testid="checkout-gst">{inr(order ? (order.gst_amount ?? 0) : pricing.gst)}</span>
              </div>
              {(() => { const fb = order ? (order.scheme?.free_boxes || 0) : items.reduce((s, i) => { const bx = i.qty * (parseInt(itemPack(i), 10) || 1); return s + (bx >= 10 ? Math.floor(bx / 2) : 0); }, 0); return fb > 0 ? <div className="flex justify-between text-sm text-emerald-700"><span className="uppercase text-[11px] tracking-[0.2em]">Free Boxes (Scheme)</span><span className="font-semibold" data-testid="checkout-free-boxes">+{fb} boxes · ₹0.00</span></div> : null; })()}
              <div className="flex justify-between pt-1.5 border-t hairline">
                <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500">Total Payable</span>
                <span className="font-semibold text-lg" data-testid="checkout-total">{inr(order ? order.total : pricing.grand)}</span>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-3">GST invoice issued with every order. Prices shown are Net Rates + 5% GST.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
